import { useMemo, useState, useContext } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { AuthContext } from "../auth/AuthContext";
import { getMyNotifications, markAllNotificationsRead, markNotificationRead } from "../api/notificationApi";
import { colors } from "../theme/colors";
import { COLORS as C, RADIUS } from "../constants/ui";
import { routeForNotification } from "../utils/notificationRoute";

const typeMeta = {
  assigned: { label: "Assigned", bg: colors.primary.bg, color: colors.primary.main },
  reassigned: { label: "Reassigned", bg: colors.warning.bg, color: colors.warning.main },
  tagged: { label: "Tagged", bg: "#f3f0ff", color: "#7c3aed" },
  join_requested: { label: "Join request", bg: C.warnSoft, color: C.warn },
  approved: { label: "Approved", bg: C.doneSoft, color: "#15803d" },
  team_joined: { label: "Team joined", bg: "#ecfdf5", color: "#047857" },
  onboarding: { label: "Onboarding", bg: C.accentSoft, color: C.accentInk },
  leave_applied: { label: "Leave applied", bg: C.warnSoft, color: C.warn },
  leave_approved: { label: "Leave approved", bg: C.doneSoft, color: "#15803d" },
};

const Notifications = () => {
  const [range, setRange] = useState("week");
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useContext(AuthContext);

  const { data, isLoading } = useQuery({
    queryKey: ["notifications", range],
    queryFn: () => getMyNotifications(range).then((r) => r.data),
  });

  const notifications = data?.notifications || [];
  const unread = data?.unread || 0;

  const markReadMutation = useMutation({
    mutationFn: (id) => markNotificationRead(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });

  const markAllMutation = useMutation({
    mutationFn: () => markAllNotificationsRead(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notifications"] });
      toast.success("All notifications marked as read");
    },
  });

  const rangeLabel = useMemo(() => {
    if (range === "day") return "Last 24 hours";
    if (range === "month") return "Last 30 days";
    return "Last 7 days";
  }, [range]);

  const openNotification = (n) => {
    if (!n.is_read) {
      markReadMutation.mutate(n.id);
    }
    navigate(routeForNotification(n, user));
  };

  return (
    <div>
      <div style={{ marginBottom: "1rem", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <h1 style={{ margin: 0, fontSize: "1.7rem", color: colors.text.primary }}>Notifications</h1>
          <p style={{ margin: "0.35rem 0 0", color: colors.text.secondary, fontSize: "0.88rem" }}>
            Assignments, leave updates, team alerts, and tagging ({rangeLabel})
          </p>
        </div>
        <button
          onClick={() => markAllMutation.mutate()}
          disabled={unread === 0 || markAllMutation.isLoading}
          style={{
            border: `1px solid ${colors.border.main}`,
            background: "#fff",
            borderRadius: 8,
            padding: "0.45rem 0.8rem",
            fontWeight: 600,
            color: unread === 0 ? colors.text.disabled : colors.text.primary,
            cursor: unread === 0 ? "not-allowed" : "pointer",
          }}
        >
          Mark all read
        </button>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: "1rem" }}>
        {[
          { value: "day", label: "24h" },
          { value: "week", label: "7 days" },
          { value: "month", label: "30 days" },
        ].map((option) => (
          <button
            key={option.value}
            onClick={() => setRange(option.value)}
            style={{
              border: `1px solid ${range === option.value ? colors.primary.main : colors.border.main}`,
              background: range === option.value ? colors.primary.bg : "#fff",
              borderRadius: 999,
              padding: "0.25rem 0.7rem",
              fontSize: "0.78rem",
              fontWeight: 700,
              color: range === option.value ? colors.primary.main : colors.text.secondary,
              cursor: "pointer",
            }}
          >
            {option.label}
          </button>
        ))}
      </div>

      <div style={{ marginBottom: "0.75rem", color: colors.text.secondary, fontSize: "0.82rem", fontWeight: 600 }}>
        Unread: {unread}
      </div>

      {isLoading ? (
        <div style={{ padding: "2rem", textAlign: "center", color: colors.text.secondary }}>Loading notifications...</div>
      ) : notifications.length === 0 ? (
        <div style={{ padding: "2rem", textAlign: "center", border: `1px solid ${colors.border.main}`, borderRadius: 12, background: "#fff", color: colors.text.secondary }}>
          No notifications found for this period.
        </div>
      ) : (
        <div style={{ display: "grid", gap: 10 }}>
          {notifications.map((n) => {
            const meta = typeMeta[n.notification_type] || { label: n.notification_type, bg: colors.gray[100], color: colors.gray[700] };
            return (
              <button
                key={n.id}
                onClick={() => openNotification(n)}
                style={{
                  textAlign: "left",
                  border: `1px solid ${n.is_read ? colors.border.main : colors.primary.light}`,
                  background: "#fff",
                  borderRadius: 12,
                  padding: "0.8rem 0.9rem",
                  cursor: "pointer",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                  <span style={{ fontWeight: 700, color: colors.text.primary }}>{n.title}</span>
                  <span style={{ fontSize: "0.72rem", color: colors.text.disabled }}>
                    {new Date(n.created_at).toLocaleString()}
                  </span>
                </div>
                <div style={{ fontSize: "0.82rem", color: colors.text.secondary, marginBottom: 6 }}>{n.message}</div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: "0.7rem", fontWeight: 700, padding: "2px 8px", borderRadius: 999, background: meta.bg, color: meta.color }}>
                    {meta.label}
                  </span>
                  {!n.is_read && (
                    <span style={{ fontSize: "0.72rem", fontWeight: 700, color: colors.primary.main }}>Unread</span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Notifications;
